import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { FieldStatusDto, SourcingEventDto } from "../../../../../../autogen/bff-api";
import type {
  ReadinessLookup,
  KnownFieldKey,
} from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../../../../../common/sourcing-events/buyer/wizardSteps";
import {
  environmentRuleApplies,
  isEnvironmentCriterionType,
  isExemption,
  MIN_ENVIRONMENT_WEIGHTING_PERCENT,
} from "../../../../../../common/procurement/publicProcurementOptions";
import {
  areInOrderOfPriority,
  climateAmongHighestPriorities,
} from "../../../../../../common/award-criteria/orderOfPriority";
import { isFoaPartIII } from "../../../../../../common/procurement/foaPartII";
import { AwardCriteriaWeightsTable } from "./AwardCriteriaWeightsTable";
import { EmptyValue, SummaryField } from "./SummaryField";
import { SummarySection } from "./SummarySection";
import { useProcurementTerms } from "../../../../../../common/terminology/useProcurementTerms";
import { priceModelType, usePriceModelDescription } from "../../../../common/priceModel";
import { requiresBestPriceQualityRatio } from "../../utils/procedure";

export const FIELD_KEYS = [
  "AwardCriterionModelSet",
  "AwardCriteriaWeightsValid",
  "AwardSubCriteriaWeightsValid",
  "AwardCriteriaDescriptionsPresent",
  "AwardSubCriteriaDescribed",
  "AwardPriceCriterionUnique",
  "EnvironmentCriterionWeightingJustified",
] as const satisfies readonly KnownFieldKey[];

const LOWEST_PRICE_IRRELEVANT: ReadonlySet<KnownFieldKey> = new Set([
  "AwardCriteriaWeightsValid",
  "AwardSubCriteriaWeightsValid",
  "AwardCriteriaDescriptionsPresent",
  "AwardSubCriteriaDescribed",
  "AwardPriceCriterionUnique",
  "EnvironmentCriterionWeightingJustified",
]);

interface AwardCriteriaSectionProps {
  event: SourcingEventDto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const AwardCriteriaSection = ({
  event,
  readinessFor,
  onWizardStepNavigate,
}: AwardCriteriaSectionProps) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const describePriceModel = usePriceModelDescription();
  const awardCriteria = event.eventData.awardCriteria;
  const isLowestPrice = awardCriteria?.lowestPrice != null;
  const priceModel = event.eventData.priceModel;
  const asksForPriceModel =
    !isLowestPrice &&
    (awardCriteria?.bestPriceQualityRatio != null ||
      requiresBestPriceQualityRatio(event.eventContext.type === "Public" ? event.eventContext.procedureType : undefined));
  const criteria = awardCriteria?.bestPriceQualityRatio?.criteria ?? [];
  const environmentJustification =
    awardCriteria?.bestPriceQualityRatio?.environmentWeightingJustification?.trim() ?? "";

  // § 5b/§ 5c: surface the justification only while it is relevant — the rule applies (Del II/III) AND
  // the summed environment weighting is still below the 30 % floor. This mirrors the step's
  // needsEnvironmentJustification, so a justification that is merely preserved (kept on the draft at
  // >= 30 % so a transient bump doesn't lose it, or left over from a since-changed part) is never shown.
  const environmentWeight = criteria
    .filter((c) => isEnvironmentCriterionType(c.type))
    .reduce((sum, c) => sum + (c.weight?.number ?? 0), 0);
  const environmentStatus = readinessFor("EnvironmentCriterionWeightingJustified");
  const ranked = areInOrderOfPriority(criteria);
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const showEnvironmentJustification =
    environmentRuleApplies(event.eventContext.type === "Public" ? event.eventContext.foaPart : undefined) &&
    (ranked ? !climateAmongHighestPriorities(criteria) : environmentWeight < MIN_ENVIRONMENT_WEIGHTING_PERCENT) &&
    !isLowestPrice;

  const fieldStatuses = FIELD_KEYS.filter(
    (k) => !(isLowestPrice && LOWEST_PRICE_IRRELEVANT.has(k)),
  )
    .map(readinessFor)
    .filter((s): s is FieldStatusDto => s !== undefined);

  const modelLabel = isLowestPrice
    ? t("Lowest price")
    : awardCriteria?.bestPriceQualityRatio
      ? t("Best price-quality ratio")
      : null;

  return (
    <SummarySection
      title={terms.awardCriteria}
      editTarget={{ kind: "wizard-step", step: "awardCriteria" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      <SummaryField
        label={terms.awardModel}
        sourcePaths={["eventData.awardCriteria.lowestPrice", "eventData.awardCriteria.bestPriceQualityRatio.selected"]}
        value={modelLabel ?? <EmptyValue text={t("Not set")} />}
        status={readinessFor("AwardCriterionModelSet")}
      />
      {asksForPriceModel && (
        <Box mt="3">
          <SummaryField
            label={t("Price model")}
            sourcePaths={["eventData.priceModel"]}
            value={
              <Box>
                <Text>{t(priceModelType(priceModel))}</Text>
                <Text mt="1" fontSize="sm" color="smMuted" fontWeight="normal">
                  {describePriceModel(priceModelType(priceModel), priceModel?.hybrid?.breakpointPercent)}
                </Text>
              </Box>
            }
          />
        </Box>
      )}
      {!isLowestPrice && criteria.length > 0 && (
        <Box mt="3">
          {/* The weight rules have no field of their own, so they hang off the table they describe —
              otherwise Publish disables with nothing on this page to point at. */}
          <SummaryField
            label={t("Criteria & weights")}
            sourcePaths={["eventData.awardCriteria.bestPriceQualityRatio.criteria"]}
            value={<AwardCriteriaWeightsTable criteria={criteria} />}
            status={readinessFor("AwardCriteriaWeightsValid")}
            extraStatuses={[
              readinessFor("AwardSubCriteriaWeightsValid"),
              readinessFor("AwardSubCriteriaDescribed"),
              readinessFor("AwardCriteriaDescriptionsPresent"),
              readinessFor("AwardPriceCriterionUnique"),
            ]}
          />
        </Box>
      )}
      {ranked && publicContext && isFoaPartIII(publicContext.regulation, publicContext.foaPart) && (
        <Box mt="3">
          <SummaryField
            label={t("Why the award criteria cannot be weighted")}
            sourcePaths={["eventData.awardCriteria.bestPriceQualityRatio.unweightedReason"]}
            value={
              awardCriteria?.bestPriceQualityRatio?.unweightedReason?.trim() ? (
                <Text whiteSpace="pre-wrap">{awardCriteria.bestPriceQualityRatio.unweightedReason}</Text>
              ) : (
                <EmptyValue text={t("Not added")} />
              )
            }
          />
        </Box>
      )}
      {showEnvironmentJustification && (
        <Box mt="3">
          <SummaryField
            label={
              isExemption(criteria)
                ? t("Justification for the exemption")
                : ranked
                  ? t("Justification for the lower priority")
                  : t("Justification for the lower weighting")
            }
            sourcePaths={["eventData.awardCriteria.bestPriceQualityRatio.environmentWeightingJustification"]}
            value={
              environmentJustification !== "" ? (
                <Text whiteSpace="pre-wrap">{environmentJustification}</Text>
              ) : (
                <EmptyValue text={t("Not added")} />
              )
            }
            status={environmentStatus}
          />
        </Box>
      )}
    </SummarySection>
  );
};
